import {
  REGISTRATION_ONBOARDING_SLIDES,
  REGISTRATION_PROGRESS_START_PERCENT,
  REGISTRATION_STEPS,
  type OnboardingSlideKey,
  type RegistrationStep,
} from './registration-flow.constants';

type RegistrationStepState = {
  currentStep: RegistrationStep;
  stepIndex: number;
  totalSteps: number;
  progressPercent: number;
  progressPercentLabel: string;
  progressEtaTranslationKey: string;
};

const FULL_PROGRESS_PERCENT = 100;
const DEFAULT_PROGRESS_ETA_TRANSLATION_KEY = 'registrationProfile.progressEta';

export function parseOnboardingSlideKey(
  rawSlide?: string | null,
): OnboardingSlideKey {
  if (!rawSlide) {
    return REGISTRATION_ONBOARDING_SLIDES[0];
  }

  if (
    REGISTRATION_ONBOARDING_SLIDES.includes(
      rawSlide as (typeof REGISTRATION_ONBOARDING_SLIDES)[number],
    )
  ) {
    return rawSlide as OnboardingSlideKey;
  }

  return REGISTRATION_ONBOARDING_SLIDES[0];
}

function getProgressPercent(stepIndex: number, totalSteps: number): number {
  if (totalSteps <= 1) {
    return FULL_PROGRESS_PERCENT;
  }

  const progressRange =
    FULL_PROGRESS_PERCENT - REGISTRATION_PROGRESS_START_PERCENT + 1;
  const progressStep = progressRange / (totalSteps - 1);
  const rawProgressPercent =
    REGISTRATION_PROGRESS_START_PERCENT + stepIndex * progressStep;

  return Math.min(FULL_PROGRESS_PERCENT, Math.ceil(rawProgressPercent));
}

function matchesRegistrationStepPath(
  step: RegistrationStep,
  pathname: string,
): boolean {
  return (
    step.route === pathname || step.routeAliases?.includes(pathname) === true
  );
}

export function resolveRegistrationStep(
  pathname: string,
  search: string,
): RegistrationStepState {
  const searchParams = new URLSearchParams(search);
  const onboardingSlide = parseOnboardingSlideKey(searchParams.get('slide'));

  const stepIndex = REGISTRATION_STEPS.findIndex(step => {
    if (!matchesRegistrationStepPath(step, pathname)) {
      return false;
    }

    if (step.route !== '/registration/onboarding') {
      return true;
    }

    return step.onboardingSlide === onboardingSlide;
  });

  const normalizedStepIndex = stepIndex >= 0 ? stepIndex : 0;
  const currentStep = REGISTRATION_STEPS[normalizedStepIndex];
  const totalSteps = REGISTRATION_STEPS.length;
  const progressPercent =
    currentStep.progressPercentOverride ??
    getProgressPercent(normalizedStepIndex, totalSteps);

  return {
    currentStep,
    stepIndex: normalizedStepIndex,
    totalSteps,
    progressPercent,
    progressPercentLabel: `${progressPercent}%`,
    progressEtaTranslationKey:
      currentStep.progressEtaTranslationKey ??
      DEFAULT_PROGRESS_ETA_TRANSLATION_KEY,
  };
}
